import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import type { RegisterData } from '../../contexts/auth-context';
import { showSuccess, showError } from '../../components/AlertSystem';

const RegisterForm: React.FC = () => {
  const { register, error, isLoading } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterData>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    phoneNumber: '',
  });
  
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    let isValid = true;

    // Email validation
    if (!formData.email) {
      errors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
      isValid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
      isValid = false;
    }

    // Confirm password
    if (formData.password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // Name validation
    if (!formData.firstName) {
      errors.firstName = 'First name is required';
      isValid = false;
    }

    if (!formData.lastName) {
      errors.lastName = 'Last name is required';
      isValid = false;
    }

    // Date of birth
    if (!formData.dateOfBirth) {
      errors.dateOfBirth = 'Date of birth is required';
      isValid = false;
    } else {
      // Check if the person is at least 18 years old
      const dob = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
        // If birthday hasn't occurred this year yet, subtract one year
        if (age - 1 < 18) {
          errors.dateOfBirth = 'You must be at least 18 years old';
          isValid = false;
        }
      } else if (age < 18) {
        errors.dateOfBirth = 'You must be at least 18 years old';
        isValid = false;
      }
    }

    // Gender
    if (!formData.gender) {
      errors.gender = 'Gender is required';
      isValid = false;
    }

    // Phone number
    if (!formData.phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.phoneNumber.replace(/[-\s]/g, ''))) {
      errors.phoneNumber = 'Please enter a valid 10-digit phone number';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await register(formData);
      // Show success notification
      showSuccess("Registration successful! Redirecting to login page...");
      // Slight delay before redirect to allow notification to be seen
      setTimeout(() => {
        navigate('/login', { state: { message: 'Registration successful! Please log in.' } });
      }, 2000);
    } catch (error: any) {
      // Error is handled in auth context
      console.error('Registration error:', error);
      showError(error.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="w-full">
      {error && (
        <div className="mb-6 p-4 bg-red-50/80 border border-red-200 text-red-700 rounded-xl shadow-sm text-sm">
          <div className="flex mb-2">
            <svg className="h-5 w-5 mr-2 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="font-medium align-middle">{error}</span>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* First Name */}
          <div>
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              First Name *
            </label>
            <div className="relative group">
              <input
                id="firstName"
                name="firstName"
                type="text"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.firstName ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              />
            </div>
            {validationErrors.firstName && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.firstName}
              </p>
            )}
          </div>
          
          {/* Last Name */}
          <div>
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Last Name *
            </label>
            <div className="relative group">
              <input
                id="lastName"
                name="lastName"
                type="text"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.lastName ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              />
            </div>
            {validationErrors.lastName && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.lastName}
              </p>
            )}
          </div>
          
          {/* Email */}
          <div className="md:col-span-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Email *
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                </svg>
              </div>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
                placeholder="you@example.com"
              />
            </div>
            {validationErrors.email && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.email}
              </p>
            )}
          </div>
          
          {/* Phone Number */}
          <div>
            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Phone Number *
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </div>
              <input
                id="phoneNumber"
                name="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.phoneNumber ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
                placeholder="(123) 456-7890"
              />
            </div>
            {validationErrors.phoneNumber && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.phoneNumber}
              </p>
            )}
          </div>
          
          {/* Date of Birth */}
          <div>
            <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Date of Birth *
            </label>
            <div className="relative group">
              <input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.dateOfBirth ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              />
            </div>
            {validationErrors.dateOfBirth && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.dateOfBirth}
              </p>
            )}
          </div>
          
          {/* Gender */}
          <div className="md:col-span-2">
            <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Gender *
            </label>
            <div className="relative group">
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.gender ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
              >
                <option value="">Select gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="non-binary">Non-binary</option>
                <option value="other">Other</option>
                <option value="prefer-not-to-say">Prefer not to say</option>
              </select>
            </div>
            {validationErrors.gender && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.gender}
              </p>
            )}
          </div>
          
          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Password *
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                className={`w-full pl-10 pr-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
                placeholder="••••••••"
              />
            </div>
            {validationErrors.password && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.password}
              </p>
            )}
          </div>
          
          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1.5 transition-colors group-focus-within:text-[#3D7A74]">
              Confirm Password *
            </label>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 group-focus-within:text-[#3D7A74] transition-colors">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full pl-10 pr-4 py-3 bg-gray-50/50 border rounded-xl text-gray-900 shadow-sm focus:ring-2 focus:ring-[#3D7A74]/20 focus:border-[#3D7A74] focus:bg-white transition-all duration-200 outline-none ${
                  validationErrors.confirmPassword ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' : 'border-gray-200'
                }`}
                placeholder="••••••••"
              />
            </div>
            {validationErrors.confirmPassword && (
              <p className="mt-1.5 text-xs text-red-500 flex items-center">
                <span className="mr-1 inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                {validationErrors.confirmPassword}
              </p>
            )}
          </div>
        </div>
        
        <div className="pt-4">
          <p className="text-sm text-gray-500 mb-5">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="text-[#3D7A74] hover:text-[#2e5c57] font-medium">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="text-[#3D7A74] hover:text-[#2e5c57] font-medium">
              Privacy Policy
            </Link>
            .
          </p>
          
          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-xl shadow-md shadow-[#3D7A74]/20 text-sm font-semibold text-white bg-[#3D7A74] hover:bg-[#32645f] hover:shadow-lg hover:shadow-[#3D7A74]/30 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3D7A74] disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:shadow-md transition-all duration-200"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating account...
              </>
            ) : (
              'Create account'
            )}
          </button>
        </div>
      </form>
      
      <div className="mt-8 relative">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-gray-200"></div>
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 bg-white text-xs text-gray-500 uppercase tracking-wider font-medium">Already have an account?</span>
        </div>
      </div>
      
      <div className="mt-6 text-center">
        <Link 
          to="/login" 
          className="inline-flex w-full justify-center py-3 px-4 border border-gray-300 rounded-xl shadow-sm bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3D7A74] transition-all duration-200"
        >
          Sign in
        </Link>
      </div>
    </div>
  );
};

export default RegisterForm; 